import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PurchaseRequestModal from './PurchaseRequestModal';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslation } from '../../locales';
import { fetchAssetByName } from '../../config/wilder_requests';
import { buildSocialCarbonIssuanceUrl } from '../../utils/socialCarbonRegistry';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 33, 45, 0.9);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #00212d 0%, #006693 50%, #00b056 100%);
  border: 2px solid #00b056;
  border-radius: 20px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 33, 45, 0.5);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 176, 86, 0.2);
  border: 2px solid #00b056;
  color: #dbfb8e;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover {
    background: #00b056;
    color: #00212d;
    transform: scale(1.1);
  }
`;

const ModalTitle = styled.h2`
  color: #dbfb8e;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 600;
  text-shadow: 2px 2px 4px rgba(0, 33, 45, 0.5);
`;

const AssetInfo = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  background: rgba(0, 33, 45, 0.3);
  border-radius: 12px;
  border: 1px solid rgba(0, 176, 86, 0.2);

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const InfoLabel = styled.span`
  color: #00b056;
  font-weight: 600;
  font-size: 0.9rem;
  min-width: 120px;
  flex-shrink: 0;
`;

const InfoValue = styled.span`
  color: #dbfb8e;
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  word-break: break-word;
  line-height: 1.4;

  @media (max-width: 480px) {
    text-align: left;
  }
`;

const InfoValueLink = styled.a`
  color: #dbfb8e;
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  word-break: break-word;
  line-height: 1.4;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: rgba(0, 176, 86, 0.35);
  text-underline-offset: 3px;
  transition: color 0.2s ease, text-decoration-color 0.2s ease;

  &:hover {
    color: #00b056;
    text-decoration-color: #00b056;
  }

  &:focus-visible {
    outline: 2px solid #00b056;
    outline-offset: 2px;
    border-radius: 4px;
  }

  @media (max-width: 480px) {
    text-align: left;
  }
`;

const QuantityHighlight = styled.div`
  background: rgba(0, 176, 86, 0.15);
  border: 2px solid #00b056;
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  margin: 1.5rem 0;
`;

const QuantityValue = styled.div`
  font-size: 2rem;
  color: #00b056;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const QuantityLabel = styled.div`
  color: #dbfb8e;
  font-size: 1rem;
  opacity: 0.9;
`;

const ActionButton = styled.button`
  background: linear-gradient(45deg, #00b056, #006693);
  color: #dbfb8e;
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 8px 25px rgba(0, 176, 86, 0.3);

  &:hover {
    background: linear-gradient(45deg, #006693, #00b056);
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(0, 176, 86, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: rgba(0, 176, 86, 0.3);
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 176, 86, 0.3);
  border-top: 4px solid #00b056;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  text-align: center;
  padding: 1rem;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(255, 107, 107, 0.3);
`;

const AssetDetailsModal = ({ isOpen, onClose, assetName }) => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  const [assetDetails, setAssetDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && assetName) {
      fetchAssetDetails();
    }
  }, [isOpen, assetName]);

  const fetchAssetDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const row = await fetchAssetByName(assetName);
      if (row) {
        setAssetDetails(row);
      } else {
        setError(t.assetModal.noAssetFound);
      }
    } catch (err) {
      console.error('Erro ao buscar Detalhes do Pacote de Créditos:', err);
      setError(err.message || t.assetModal.error);
    } finally {
      setLoading(false);
    }
  };

  const na = () => t.assetModal.notAvailable;

  const formatDate = (dateString) => {
    if (!dateString) return na();
    try {
      const date = new Date(dateString);
      const locale = language === 'pt' ? 'pt-BR' : 'en-US';
      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return na();
    }
  };

  const formatNumber = (number) => {
    if (!number && number !== 0) return '0';
    const locale = language === 'pt' ? 'pt-BR' : 'en-US';
    return new Intl.NumberFormat(locale).format(number);
  };

  const handlePurchaseRequest = () => {
    setIsPurchaseModalOpen(true);
  };

  const closePurchaseModal = () => {
    setIsPurchaseModalOpen(false);
  };

  const assetRegistryUrl = buildSocialCarbonIssuanceUrl(assetDetails?._id);

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={onClose}>&times;</CloseButton>
          
          <ModalTitle>{t.assetModal.title}</ModalTitle>

          {loading && (
            <LoadingSpinner>
              <Spinner />
            </LoadingSpinner>
          )}

          {error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}

          {assetDetails && (
            <>
              <AssetInfo>
                <InfoRow>
                  <InfoLabel>{t.assetModal.assetName}:</InfoLabel>
                  <InfoValue>{assetDetails['Asset Name'] || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.issuer}:</InfoLabel>
                  <InfoValue>{assetDetails.Issuer || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.standard}:</InfoLabel>
                  <InfoValue>{assetDetails.Standard || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.assetType}:</InfoLabel>
                  <InfoValue>{assetDetails['Asset Type'] || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.projectType}:</InfoLabel>
                  <InfoValue>{assetDetails['Project type'] || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.vintage}:</InfoLabel>
                  <InfoValue>{assetDetails.Vintage ?? na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.serialNumberBatch}:</InfoLabel>
                  <InfoValue>
                    {assetDetails['Serial Number Batch'] || na()}
                  </InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.tokenAddress}:</InfoLabel>
                  <InfoValue>{assetDetails['Token Address'] || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.txid}:</InfoLabel>
                  <InfoValue>{assetDetails.txid || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.createdDate}:</InfoLabel>
                  <InfoValue>{formatDate(assetDetails['Created Date'])}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.modifiedDate}:</InfoLabel>
                  <InfoValue>{formatDate(assetDetails['Modified Date'])}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.createdBy}:</InfoLabel>
                  <InfoValue>{assetDetails['Created By'] || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.assetId}:</InfoLabel>
                  {assetRegistryUrl ? (
                    <InfoValueLink
                      href={assetRegistryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t.assetModal.viewOnRegistry}
                    >
                      {assetDetails._id}
                    </InfoValueLink>
                  ) : (
                    <InfoValue>{na()}</InfoValue>
                  )}
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.issuanceId}:</InfoLabel>
                  <InfoValue>{assetDetails.Issuance || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.projectId}:</InfoLabel>
                  <InfoValue>{assetDetails.Project || na()}</InfoValue>
                </InfoRow>
              </AssetInfo>

              <QuantityHighlight>
                <QuantityValue>
                  {formatNumber(
                    assetDetails["Quantity issued"] ?? assetDetails.Quantity
                  )}
                </QuantityValue>
                <QuantityLabel>{t.assetModal.creditsIssued}</QuantityLabel>
              </QuantityHighlight>

              <ActionButton onClick={handlePurchaseRequest}>
                {t.assetModal.purchaseButton}
              </ActionButton>
            </>
          )}
        </ModalContent>
      </ModalOverlay>

      <PurchaseRequestModal
        isOpen={isPurchaseModalOpen}
        onClose={closePurchaseModal}
        packageName={assetDetails?.['Asset Name'] || assetName}
      />
    </>
  );
};

export default AssetDetailsModal;
